import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { Subscription } from 'rxjs/Subscription';

import { ProductsService } from '../products.service';
import { NotificationService } from '../../../../../core/services/notification.service';
import { ConfigService } from '../../../../../core/services/config.service';

import { ProductDetails } from './product-details.model';

@Component({
  selector: 'itpx-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.css']
})
export class ProductDetailsComponent implements OnInit {

    private details: ProductDetails;
    private sub: Subscription;
    private id: string;
    private isInProgress: boolean = false;

    private baseFields: Array<any>;
    private creditFields: Array<any>;
    private detailsFields: Array<any>;
    private instructionsFields: Array<any>;
    private pricingFields: Array<any>;
    private renderFields: Array<any>;
    private salesInfoFields: Array<any>;
    private systemInfoFields: Array<any>;
    private courseInfoFields: Array<any>;
    private examInfoFields: Array<any>;

    private buttons: Array<any> = [
        {
            title: 'Edit',
            action: 'edit',
            icon: 'fa-pencil-square',
            class: 'btn-primary',
            disabled: false
        },
        {
            title: 'Delete',
            action: 'delete',
            icon: 'fa-trash',
            class: 'btn-danger',
            disabled: false
        }
    ];

    constructor(
        private productsService: ProductsService,
        private notificationService: NotificationService,
        private configService: ConfigService,
        private route: ActivatedRoute,
        private router: Router,
    ) { }

    ngOnInit() {
        this.sub = this.route.params.subscribe(params => {
            this.id = params['id'];
            this.productsService.getProductDetails(this.id)
                            .subscribe(
                                data => this.extractData(data)
                            );
        });

        this.baseFields = this.getDetailsBaseFields();
        this.creditFields = this.getDetailsCreditFields();
        this.detailsFields = this.getDetailsDetailsFields();
        this.instructionsFields = this.getDetailsInstructionsFields();
        this.pricingFields = this.getDetailsPricingFields();
        this.renderFields = this.getDetailsRenderFields();
        this.salesInfoFields = this.getDetailsSalesInfoFields();
        this.courseInfoFields = this.getDetailsCourseInfoFields();
        this.examInfoFields = this.getDetailsExamInfoFields();
    }

    ngOnDestroy() {
        this.sub.unsubscribe();
    }

    private extractData(body: any) {
        this.details = <ProductDetails>body.data;
    }

    private onAction(action: string) {
        this.isInProgress = true;
        if (action == 'edit') {
            this.router.navigate(['/agora/product-data/products/:id/edit', { id: this.id }]);
        } else if (action == 'delete') {
            this.productsService.delete(this.details)
                        .subscribe(
                            data => {
                                this.isInProgress = false;
                                this.notificationService.success(
                                    'Successfully deleted Product',
                                    'Success'
                                );
                                this.router.navigate(['/agora/product-data/products']);
                            },
                            error => this.isInProgress = false
                        );
        } else {
            return false;
        }
    }

    getDetailsBaseFields(): Array<any> {
        const productGroupRoute = '/agora/product-data/product-groups/{{keyRoute}}/details';
        let fields= [
            {
                key: 'name',
                label: 'Name',
                type: 'text'
            }, {
                key: 'sku',
                label: 'SKU',
                type: 'text'
            }, {
                key: 'type.configValue',
                label: 'Product Type',
                type: 'text'
            }, {
                key: 'shortDescription',
                label: 'Short Description',
                type: 'text'
            }, {
                key: 'group.name',
                label: 'Product Group',
                type: 'link',
                config: {
                    route: productGroupRoute,
                    keyRoute: 'group.id'
                }
            }, {
                key: 'segment.name',
                label: 'Product Segment',
                type: 'text'
            }, {
                key: 'keywords',
                label: 'Keywords',
                type: 'list'
            }, {
                key: (e: any) => {
                    let result = [];
                    if(e.domains && e.domains.length) e.domains.forEach(element => {
                        result.push(element.configValue);
                    });
                    return result;
                },
                label: 'Domains',
                type: 'list'
            }, {
                key: (e: any) => {
                    let result = [];
                    if(e.categoryFamily && e.categoryFamily.length) e.categoryFamily.forEach(element => {
                        result.push(element.configValue);
                    });
                    return result;
                },
                label: 'Category Family',
                type: 'list'
            }, {
                key: 'quotation',
                label: 'Quotation',
                type: 'map',
                map : {
                    true: 'Yes',
                    false: 'No'
                }
            }, {
                key: 'online',
                label: 'Online',
                type: 'map',
                map : {
                    true: 'Yes',
                    false: 'No'
                }
            }, {
                key: 'available',
                label: 'Available',
                type: 'map',
                map : {
                    true: 'Yes',
                    false: 'No'
                }
            }, {
            // As part of Issue Issue EC-152, Enriched tile should be hidden
            //     key: 'enriched',
            //     label: 'Enriched',
            //     type: 'map',
            //     map : {
            //         true: 'Yes',
            //         false: 'No'
            //     }
            // }, {
                key: 'editableCourseware',
                label: 'Editable Courseware',
                type: 'map',
                map : {
                    true: 'Yes',
                    false: 'No'
                }
            }, {
                key: 'level.configValue',
                label: 'Level',
                type: 'text'
            }, {
                key: (e: any) => {
                    let result;
                    let brand = e.brand;
                    if(brand && brand.image) {
                        result = '<li class="details-brand-element"><img src="'+
                            brand.image + '"/><span>'+ brand.name +'</span></li>';
                    } else if(brand && !brand.image) {
                        result = '<li class="details-brand-element"><span>'+ brand.name +'</span></li>';
                    } else {
                        result = null
                    }
                    return result;
                },
                label: 'Brand',
                type: 'innerHtml'
            },  {
                key: 'hosted',
                label: 'Hosted By',
                type: 'map',
                getter: (item) => {
                    if (item && item.hosted) return 'Leapest';
                    else if (item && item.hosted === false) return 'External';
                    else return null
                }
            }
        ];
        return fields;
    }

    getDetailsCreditFields(): Array<any> {
        let fields = [
            {
                key: 'credits.royaltyPoints',
                label: 'Royalty Points',
                type: 'text'
            },
            {
                key: 'credits.credits',
                label: 'Credits',
                type: 'text'
            },
            {
                key: 'credits.pmiCredits',
                label: 'PMI Credits',
                type: 'text'
            },
            {
                key: 'credits.apdCredits',
                label: 'APD Credits',
                type: 'text'
            }
        ];
        return fields;
    }

    getDetailsDetailsFields(): Array<any> {
       let fields = [
            {
                key: 'details.accreditor',
                label: 'Accreditor',
                type: 'text'
            },
            {
                key: 'details.releaseNote',
                label: 'Release Note',
                type: 'text'
            },
            {
                key: 'details.anticipatedNextRelease',
                label: 'Anticipated Next Release',
                type: 'text'
            },
            {
                key: 'details.sampleFileUri',
                label: 'Sample File Url',
                type: 'text'
            },
            {
                key: 'details.leadAuthor',
                label: 'Lead Author',
                type: 'text'
            },
            {
                key: 'details.coAuthor',
                label: 'Co-Author',
                type: 'text'
            },
            {
                key: 'details.qualityBrand',
                label: 'Quality Brand',
                type: 'text'
            },
            {
                id: 'longDescription',
                key: 'details.longDescription',
                label: 'Long Description',
                type: 'html'
            },
            {
                id: 'ipStatement',
                key: 'details.ipStatement',
                label: 'IP Statement',
                type: 'html'
            },
            {
                key: 'details.features',
                label: 'Features',
                type: 'text'
            },
            {
                key: 'details.version',
                label: 'Version',
                type: 'text'
            },
            {
                id: 'sellerStatement',
                key: 'instructions.sellerStatement',
                label: 'Seller Statement',
                type: 'html'
            }

        ];
        return fields;
    }

    getDetailsInstructionsFields(): Array<any> {
        let fields= [
            {
                key: 'instructions.examLocations',
                label: 'Exam Locations',
                type: 'text'
            }, {
                id: 'prerequisites',
                key: 'instructions.prerequisites',
                label: 'Prerequisites',
                type: 'html'
            },
            {
                id: 'openBookExamRequirements',
                key: 'instructions.openBookExamRequirements',
                label: 'Open Book Exam Requirements',
                type: 'html'
            },
            {
                id: 'instructorRequirements',
                key: 'instructions.instructorRequirements',
                label: 'Instructor Requirements',
                type: 'html'
            },
            {
                id: 'proctorRequirements',
                key: 'instructions.proctorRequirements',
                label: 'Proctor Requirements',
                type: 'html'
            },
            {
                key: 'instructions.technicalRequirements',
                label: 'Technical Requirements',
                type: 'text'
            }
        ];
        return fields;
    }

    getDetailsPricingFields(): Array<any> {
        let fields= [
            {
                key: 'pricing.listPrice',
                label: 'List Price',
                type: 'text'
            },
            {
                key: (e: any) => {
                    return e.pricing.discountCategory ? e.pricing.discountCategory.configValue : '';
                },
                label: 'Discount Category',
                type: 'text'
            }
        ];
        return fields;
    }

    getDetailsRenderFields(): Array<any> {
        let fields= [
            {
                key: 'render.largeImageUri',
                label: 'Large Image Url',
                type: 'text'
            },
            {
                key: 'render.mediumImageUri',
                label: 'Medium Image Url',
                type: 'text'
            },
            {
                key: 'render.smallImageUri',
                label: 'Small Image Url',
                type: 'text'
            }
        ];
        return fields;
    }

    getDetailsSalesInfoFields(): Array<any> {
        let fields= [
            {
                key: 'salesInfo.baseUnit',
                label: 'Base Unit',
                type: 'text'
            },
            {
                key: 'salesInfo.packingUnit',
                label: 'Packing Unit',
                type: 'text'
            },
            {
                key: 'salesInfo.puBuRatio',
                label: 'PU BU Ratio',
                type: 'text'
            },
            {
                key: 'salesInfo.eanCode',
                label: 'EAN Code',
                type: 'text'
            },
            {
                key: 'salesInfo.minimumOrderQuantity',
                label: 'Minimum Order Quantity',
                type: 'text'
            },
            {
                key: 'salesInfo.maximumOrderQuantity',
                label: 'Maximum Order Quantity',
                type: 'text'
            },
            {
                key: 'salesInfo.stepQuantity',
                label: 'Step Quantity',
                type: 'text'
            }
        ];
        return fields;
    }

    getDetailsCourseInfoFields(): Array<any> {
        let fields= [
            {
                id: 'agenda',
                key: 'courseInfo.agenda',
                label: 'Agenda',
                type: 'html'
            },
            {
                id: 'description',
                key: 'courseInfo.description',
                label: 'Description',
                type: 'html'
            },
            {
                id: 'outline',
                key: 'courseInfo.outline',
                label: 'Outline',
                type: 'html'
            },
            {
                id: 'learningObjectives',
                key: 'courseInfo.learningObjectives',
                label: 'Learning Objectives',
                type: 'html'
            },
            {
                id: 'prerequisites',
                key: 'courseInfo.prerequisites',
                label: 'Prerequisites',
                type: 'html'
            },
            {
                key: 'courseInfo.targetAudience',
                label: 'Target Audience',
                type: 'text'
            },
            {
                key: 'courseInfo.duration',
                label: 'Duration',
                type: 'text'
            },
            {
                key: (e: any) => {
                    return e.courseInfo.durationType ? e.courseInfo.durationType.configValue : '';
                },
                label: 'Duration Type',
                type: 'text'
            }
        ];
        return fields;
    }

    getDetailsExamInfoFields(): Array<any> {
        let fields= [
            {
                key: 'examInfo.extraTime',
                label: 'Extra Time',
                type: 'text'
            }, {
                key: 'examInfo.fulfillment',
                label: 'Fulfillment',
                type: 'text'
            }, {
                id: 'planning',
                key: 'examInfo.planning',
                label: 'Planning',
                type: 'html'
            }, {
                key: 'examInfo.questions',
                label: 'Questions',
                type: 'text'
            }, {
                key: 'examInfo.passGrade',
                label: 'Pass Grade',
                type: 'text'
            }, {
                key: 'examInfo.passGradeInstructors',
                label: 'Pass Grade Instructors',
                type: 'text'
            }, {
                key: 'examInfo.questionFormat',
                label: 'Question Format',
                type: 'text'
            }
        ];
        return fields;
    }
}



// WEBPACK FOOTER //
// ./src/app/agora/components/product-data/products/details/product-details.component.ts